import { useState } from 'react'
import {
  loadFileSystem,
  saveFileSystem,
  resetFileSystem,
  getIconForFilename
} from '../lib/defaultFileSystem'

const ExplorerApp = ({ onOpenApp }) => {
  const [fs, setFs] = useState(loadFileSystem)
  const [currentPath, setCurrentPath] = useState('C:\\Users\\PinkPixel')
  const [selectedItem, setSelectedItem] = useState(null)
  const [viewMode, setViewMode] = useState('grid') // 'grid' or 'list'
  
  // Modal states for File System operations
  const [activeModal, setActiveModal] = useState(null) // 'newFile' | 'newFolder' | 'rename' | 'delete' | 'reset'
  const [modalInput, setModalInput] = useState('')
  const [modalError, setModalError] = useState('')

  // Helper to locate a folder node given a path string
  const getFolderNode = (fileSystemTree, targetPath) => {
    let current = fileSystemTree['C:\\Users\\PinkPixel']
    if (!current) return null

    const pathParts = targetPath.split('\\').filter(Boolean)
    const folderParts = pathParts.slice(3) // Skip C:, Users, PinkPixel

    for (const part of folderParts) {
      if (current.items && current.items[part] && current.items[part].type === 'folder') {
        current = current.items[part]
      } else {
        return null
      }
    }
    return current
  }

  const getCurrentItems = () => {
    const node = getFolderNode(fs, currentPath)
    if (!node) {
      return {}
    }
    return node.items || {}
  }

  const navigateToPath = (newPath) => {
    setCurrentPath(newPath)
    setSelectedItem(null)
  }

  const handleItemClick = (itemName, item) => {
    if (item.type === 'folder') {
      const newPath = `${currentPath}\\${itemName}`
      navigateToPath(newPath)
    } else {
      setSelectedItem({ name: itemName, ...item })
    }
  }

  const handleOpenFile = (item) => {
    if (!item) return
    if (item.targetApp) {
      onOpenApp?.(item.targetApp)
      return
    }
    const name = (item.name || '').toLowerCase()
    if (name.endsWith('.txt') || name.endsWith('.md')) {
      onOpenApp?.('notepad')
    } else if (name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.webp')) {
      onOpenApp?.('photos')
    } else if (name.endsWith('.mp3') || name.endsWith('.m3u')) {
      onOpenApp?.('spotify')
    } else if (name.endsWith('.pdf')) {
      onOpenApp?.('browser')
    } else if (name === 'pink_pixel_os') {
      onOpenApp?.('settings')
    } else {
      onOpenApp?.('notepad')
    }
  }

  const handleItemDoubleClick = (name, item) => {
    if (item.type === 'folder') {
      handleItemClick(name, item)
    } else {
      handleOpenFile({ name, ...item })
    }
  }

  const goUp = () => {
    const pathParts = currentPath.split('\\')
    if (pathParts.length > 3) {
      pathParts.pop()
      navigateToPath(pathParts.join('\\'))
    }
  }

  // File system operations
  const openNewFileModal = () => {
    setModalInput('New_Document.txt')
    setModalError('')
    setActiveModal('newFile')
  }

  const openNewFolderModal = () => {
    setModalInput('New_Folder')
    setModalError('')
    setActiveModal('newFolder')
  }

  const openRenameModal = () => {
    if (!selectedItem) return
    setModalInput(selectedItem.name)
    setModalError('')
    setActiveModal('rename')
  }

  const openDeleteModal = () => {
    if (!selectedItem) return
    setModalError('')
    setActiveModal('delete')
  }

  const openResetModal = () => {
    setModalError('')
    setActiveModal('reset')
  }

  const handleCreateFile = () => {
    const name = modalInput.trim()
    if (!name) {
      setModalError('File name cannot be empty')
      return
    }
    if (name.includes('\\') || name.includes('/')) {
      setModalError('File name cannot contain slashes')
      return
    }

    const currentItems = getCurrentItems()
    if (currentItems[name]) {
      setModalError('An item with this name already exists')
      return
    }

    const nextFs = JSON.parse(JSON.stringify(fs))
    const folder = getFolderNode(nextFs, currentPath)
    if (!folder) return

    if (!folder.items) folder.items = {}
    folder.items[name] = {
      type: 'file',
      icon: getIconForFilename(name),
      size: '1 KB',
      modified: new Date().toISOString().slice(0, 10),
      description: 'Created by user'
    }

    setFs(nextFs)
    saveFileSystem(nextFs)
    setActiveModal(null)
  }

  const handleCreateFolder = () => {
    const name = modalInput.trim()
    if (!name) {
      setModalError('Folder name cannot be empty')
      return
    }
    if (name.includes('\\') || name.includes('/')) {
      setModalError('Folder name cannot contain slashes')
      return
    }

    const currentItems = getCurrentItems()
    if (currentItems[name]) {
      setModalError('A folder with this name already exists')
      return
    }

    const nextFs = JSON.parse(JSON.stringify(fs))
    const folder = getFolderNode(nextFs, currentPath)
    if (!folder) return

    if (!folder.items) folder.items = {}
    folder.items[name] = {
      type: 'folder',
      icon: 'fas fa-folder text-blue-400',
      items: {}
    }

    setFs(nextFs)
    saveFileSystem(nextFs)
    setActiveModal(null)
  }

  const handleRename = () => {
    if (!selectedItem) return
    const newName = modalInput.trim()
    if (!newName) {
      setModalError('Name cannot be empty')
      return
    }
    if (newName.includes('\\') || newName.includes('/')) {
      setModalError('Name cannot contain slashes')
      return
    }
    if (newName === selectedItem.name) {
      setActiveModal(null)
      return
    }

    const currentItems = getCurrentItems()
    if (currentItems[newName]) {
      setModalError('An item with this name already exists')
      return
    }

    const nextFs = JSON.parse(JSON.stringify(fs))
    const folder = getFolderNode(nextFs, currentPath)
    if (!folder || !folder.items) return

    const itemData = folder.items[selectedItem.name]
    delete folder.items[selectedItem.name]
    
    // Update icon if renaming a file
    if (itemData.type === 'file') {
      itemData.icon = getIconForFilename(newName)
    }
    itemData.modified = new Date().toISOString().slice(0, 10)
    folder.items[newName] = itemData

    setFs(nextFs)
    saveFileSystem(nextFs)
    setSelectedItem({ name: newName, ...itemData })
    setActiveModal(null)
  }

  const handleDelete = () => {
    if (!selectedItem) return

    const nextFs = JSON.parse(JSON.stringify(fs))
    const folder = getFolderNode(nextFs, currentPath)
    if (!folder || !folder.items) return

    delete folder.items[selectedItem.name]

    setFs(nextFs)
    saveFileSystem(nextFs)
    setSelectedItem(null)
    setActiveModal(null)
  }

  const handleResetFileSystem = () => {
    const fresh = resetFileSystem()
    setFs(fresh)
    setSelectedItem(null)
    setCurrentPath('C:\\Users\\PinkPixel')
    setActiveModal(null)
  }

  const sidebarItems = [
    { icon: 'fas fa-desktop text-blue-400', name: 'Desktop', path: 'C:\\Users\\PinkPixel\\Desktop' },
    { icon: 'fas fa-folder text-purple-400', name: 'Projects', path: 'C:\\Users\\PinkPixel\\Projects' },
    { icon: 'fas fa-file-alt text-blue-400', name: 'Documents', path: 'C:\\Users\\PinkPixel\\Documents' },
    { icon: 'fas fa-image text-pink-400', name: 'Pictures', path: 'C:\\Users\\PinkPixel\\Pictures' },
    { icon: 'fas fa-music text-purple-400', name: 'Music', path: 'C:\\Users\\PinkPixel\\Music' },
    { icon: 'fas fa-download text-green-400', name: 'Downloads', path: 'C:\\Users\\PinkPixel\\Downloads' },
  ]

  const breadcrumbs = currentPath.split('\\').filter(Boolean)
  const currentItems = getCurrentItems()
  const itemCount = Object.keys(currentItems).length

  return (
    <div className="flex flex-col h-full relative">
      {/* Modal Dialog for VFS Operations */}
      {activeModal && (
        <div className="absolute inset-0 z-30 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-sm bg-gray-900 border border-white/20 rounded-xl shadow-2xl p-5 space-y-4 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <h3 className="text-sm font-bold text-white flex items-center gap-2">
                {activeModal === 'newFile' && <><i className="fas fa-file-plus text-pink-400"></i> New File</>}
                {activeModal === 'newFolder' && <><i className="fas fa-folder-plus text-blue-400"></i> New Folder</>}
                {activeModal === 'rename' && <><i className="fas fa-edit text-amber-400"></i> Rename Item</>}
                {activeModal === 'delete' && <><i className="fas fa-trash-alt text-rose-400"></i> Confirm Delete</>}
                {activeModal === 'reset' && <><i className="fas fa-rotate-left text-amber-400"></i> Reset File System</>}
              </h3>
              <button 
                onClick={() => setActiveModal(null)}
                className="text-gray-400 hover:text-white p-1 rounded transition-colors"
              >
                <i className="fas fa-times text-xs"></i>
              </button>
            </div>

            {/* Input field for create / rename */}
            {(activeModal === 'newFile' || activeModal === 'newFolder' || activeModal === 'rename') && (
              <div>
                <label className="block text-xs text-gray-300 mb-1.5">
                  {activeModal === 'newFile' ? 'Enter file name with extension:' : 'Enter name:'}
                </label>
                <input
                  type="text"
                  value={modalInput}
                  onChange={(e) => {
                    setModalInput(e.target.value)
                    setModalError('')
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      if (activeModal === 'newFile') handleCreateFile()
                      else if (activeModal === 'newFolder') handleCreateFolder()
                      else if (activeModal === 'rename') handleRename()
                    } else if (e.key === 'Escape') {
                      setActiveModal(null)
                    }
                  }}
                  autoFocus
                  className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-pink-500"
                />
              </div>
            )}

            {/* Delete confirmation message */}
            {activeModal === 'delete' && (
              <p className="text-sm text-gray-300">
                Are you sure you want to permanently delete <span className="font-semibold text-white">"{selectedItem?.name}"</span>?
              </p>
            )}

            {/* Reset confirmation message */}
            {activeModal === 'reset' && (
              <p className="text-sm text-gray-300">
                Are you sure you want to restore all default files and folders? Custom files created in this session will be replaced.
              </p>
            )}

            {modalError && (
              <p className="text-xs text-rose-400 flex items-center gap-1.5">
                <i className="fas fa-circle-exclamation"></i>
                <span>{modalError}</span>
              </p>
            )}

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/10">
              <button
                onClick={() => setActiveModal(null)}
                className="px-3 py-1.5 text-xs text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
              >
                Cancel
              </button>
              {activeModal === 'newFile' && (
                <button
                  onClick={handleCreateFile}
                  className="px-3 py-1.5 text-xs text-white bg-pink-600 hover:bg-pink-500 rounded-lg font-medium transition-colors"
                >
                  Create File
                </button>
              )}
              {activeModal === 'newFolder' && (
                <button
                  onClick={handleCreateFolder}
                  className="px-3 py-1.5 text-xs text-white bg-blue-600 hover:bg-blue-500 rounded-lg font-medium transition-colors"
                >
                  Create Folder
                </button>
              )}
              {activeModal === 'rename' && (
                <button
                  onClick={handleRename}
                  className="px-3 py-1.5 text-xs text-white bg-amber-600 hover:bg-amber-500 rounded-lg font-medium transition-colors"
                >
                  Rename
                </button>
              )}
              {activeModal === 'delete' && (
                <button
                  onClick={handleDelete}
                  className="px-3 py-1.5 text-xs text-white bg-rose-600 hover:bg-rose-500 rounded-lg font-medium transition-colors"
                >
                  Delete
                </button>
              )}
              {activeModal === 'reset' && (
                <button
                  onClick={handleResetFileSystem}
                  className="px-3 py-1.5 text-xs text-white bg-amber-600 hover:bg-amber-500 rounded-lg font-medium transition-colors"
                >
                  Reset Defaults
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2 p-2.5 bg-white/5 rounded-t-lg border-b border-white/10">
        <div className="flex items-center gap-2">
          <button 
            onClick={goUp}
            disabled={currentPath === 'C:\\Users\\PinkPixel'}
            className="px-2.5 py-1 bg-white/10 rounded hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed text-xs transition-colors"
            title="Go to parent directory"
          >
            <i className="fas fa-arrow-up mr-1"></i>
            Up
          </button>

          <div className="flex items-center gap-1 text-xs overflow-x-auto max-w-[200px] sm:max-w-md">
            {breadcrumbs.map((part, index) => (
              <div key={index} className="flex items-center shrink-0">
                <span 
                  className="text-blue-300 cursor-pointer hover:text-blue-200"
                  onClick={() => navigateToPath(breadcrumbs.slice(0, index + 1).join('\\'))}
                >
                  {part}
                </span>
                {index < breadcrumbs.length - 1 && <i className="fas fa-chevron-right mx-1 text-white/40 text-[10px]"></i>}
              </div>
            ))}
          </div>
        </div>

        {/* Action buttons: New File, New Folder, Rename, Delete */}
        <div className="flex items-center gap-1.5">
          <button
            onClick={openNewFileModal}
            className="px-2.5 py-1 bg-pink-500/20 hover:bg-pink-500/30 border border-pink-400/30 text-pink-300 rounded text-xs transition-colors flex items-center gap-1"
            title="Create new file"
          >
            <i className="fas fa-file-circle-plus"></i>
            <span className="hidden sm:inline">New File</span>
          </button>

          <button
            onClick={openNewFolderModal}
            className="px-2.5 py-1 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-400/30 text-blue-300 rounded text-xs transition-colors flex items-center gap-1"
            title="Create new folder"
          >
            <i className="fas fa-folder-plus"></i>
            <span className="hidden sm:inline">New Folder</span>
          </button>

          {selectedItem && (
            <>
              <button
                onClick={openRenameModal}
                className="px-2.5 py-1 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/30 text-amber-300 rounded text-xs transition-colors flex items-center gap-1"
                title="Rename selected item"
              >
                <i className="fas fa-pen-to-square"></i>
                <span className="hidden sm:inline">Rename</span>
              </button>

              <button
                onClick={openDeleteModal}
                className="px-2.5 py-1 bg-rose-500/20 hover:bg-rose-500/30 border border-rose-400/30 text-rose-300 rounded text-xs transition-colors flex items-center gap-1"
                title="Delete selected item"
              >
                <i className="fas fa-trash"></i>
                <span className="hidden sm:inline">Delete</span>
              </button>
            </>
          )}

          <div className="h-4 w-px bg-white/10 mx-1 hidden sm:block"></div>

          <button 
            onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
            className="px-2.5 py-1 bg-white/10 rounded hover:bg-white/20 text-xs transition-colors"
            title={`Switch to ${viewMode === 'grid' ? 'List' : 'Grid'} view`}
          >
            <i className={`fas ${viewMode === 'grid' ? 'fa-list' : 'fa-th'}`}></i>
          </button>

          <button
            onClick={openResetModal}
            className="px-2 py-1 bg-white/5 hover:bg-white/15 text-gray-400 hover:text-amber-300 rounded text-xs transition-colors"
            title="Restore default file system"
          >
            <i className="fas fa-rotate-left"></i>
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <div className="hidden sm:block w-44 bg-white/5 p-3 border-r border-white/10 overflow-y-auto">
          <div className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">Quick Access</div>
          <div className="space-y-1">
            {sidebarItems.map((item, index) => (
              <div 
                key={index} 
                className={`p-2 rounded-lg hover:bg-white/10 flex items-center cursor-pointer transition-colors ${
                  currentPath === item.path ? 'bg-blue-500/20 text-blue-300 font-medium' : 'text-gray-300'
                }`}
                onClick={() => navigateToPath(item.path)}
              >
                <i className={`${item.icon} mr-2 text-sm w-4 text-center`}></i>
                <span className="text-xs truncate">{item.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* File List */}
          <div className="flex-1 p-4 overflow-y-auto">
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {Object.entries(currentItems).map(([name, item]) => (
                  <div 
                    key={name}
                    className={`flex flex-col items-center p-3 rounded-xl cursor-pointer transition-all hover:bg-white/10 ${
                      selectedItem?.name === name ? 'bg-blue-500/20 border border-blue-400/50' : 'border border-transparent'
                    }`}
                    onClick={() => handleItemClick(name, item)}
                    onDoubleClick={() => handleItemDoubleClick(name, item)}
                  >
                    <div className="mb-2">
                      <i className={`${item.icon || (item.type === 'folder' ? 'fas fa-folder text-blue-400' : 'fas fa-file text-gray-400')} text-3xl`}></i>
                    </div>
                    <span className="text-xs text-center break-words max-w-full leading-tight line-clamp-2">{name}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-1">
                <div className="grid grid-cols-4 gap-4 p-2 text-xs font-semibold text-white/50 border-b border-white/10">
                  <span>Name</span>
                  <span>Modified</span>
                  <span>Type</span>
                  <span>Size</span>
                </div>
                {Object.entries(currentItems).map(([name, item]) => (
                  <div 
                    key={name}
                    className={`grid grid-cols-4 gap-4 p-2 rounded-lg cursor-pointer transition-all hover:bg-white/10 ${
                      selectedItem?.name === name ? 'bg-blue-500/20' : ''
                    }`}
                    onClick={() => handleItemClick(name, item)}
                    onDoubleClick={() => handleItemDoubleClick(name, item)}
                  >
                    <div className="flex items-center min-w-0">
                      <i className={`${item.icon || (item.type === 'folder' ? 'fas fa-folder text-blue-400' : 'fas fa-file text-gray-400')} mr-2 text-sm shrink-0`}></i>
                      <span className="text-xs truncate">{name}</span>
                    </div>
                    <span className="text-xs text-white/70 truncate">{item.modified || '-'}</span>
                    <span className="text-xs text-white/70 capitalize truncate">{item.type}</span>
                    <span className="text-xs text-white/70 truncate">{item.size || '-'}</span>
                  </div>
                ))}
              </div>
            )}

            {Object.keys(currentItems).length === 0 && (
              <div className="flex flex-col items-center justify-center h-48 text-white/40 space-y-3">
                <i className="fas fa-folder-open text-4xl"></i>
                <span className="text-xs">This folder is empty</span>
                <button
                  onClick={openNewFileModal}
                  className="px-3 py-1 bg-white/10 hover:bg-white/20 rounded-lg text-xs text-white transition-colors"
                >
                  Create a file here
                </button>
              </div>
            )}
          </div>

          {/* Details Panel */}
          {selectedItem && (
            <div className="w-64 sm:w-72 bg-white/5 border-l border-white/10 p-4 flex flex-col justify-between overflow-y-auto">
              <div className="space-y-4">
                <div className="text-base font-semibold flex items-center gap-2">
                  <i className={`${selectedItem.icon} text-lg`}></i>
                  <span className="truncate">{selectedItem.name}</span>
                </div>
                
                <div className="space-y-2 text-xs bg-black/20 p-3 rounded-lg border border-white/5">
                  <div className="flex justify-between">
                    <span className="text-white/50">Type:</span>
                    <span className="capitalize font-medium text-white">{selectedItem.type}</span>
                  </div>
                  
                  {selectedItem.size && (
                    <div className="flex justify-between">
                      <span className="text-white/50">Size:</span>
                      <span className="text-white">{selectedItem.size}</span>
                    </div>
                  )}
                  
                  {selectedItem.modified && (
                    <div className="flex justify-between">
                      <span className="text-white/50">Modified:</span>
                      <span className="text-white">{selectedItem.modified}</span>
                    </div>
                  )}
                  
                  {selectedItem.description && (
                    <div className="pt-2 border-t border-white/10">
                      <span className="text-white/50 block mb-1">Details:</span>
                      <span className="text-pink-300 leading-relaxed block">{selectedItem.description}</span>
                    </div>
                  )}
                </div>

                {/* Open / Launch action */}
                <button
                  onClick={() => handleOpenFile(selectedItem)}
                  className="w-full py-2 px-3 bg-pink-500/20 hover:bg-pink-500/30 border border-pink-400/30 rounded-lg text-xs font-semibold text-pink-200 transition-colors flex items-center justify-center gap-2"
                >
                  <i className="fas fa-arrow-up-right-from-square text-xs"></i>
                  <span>{selectedItem.type === 'shortcut' ? 'Launch Application' : 'Open File'}</span>
                </button>
              </div>

              {/* Item management actions */}
              <div className="pt-4 border-t border-white/10 flex gap-2">
                <button
                  onClick={openRenameModal}
                  className="flex-1 py-1.5 px-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-xs text-gray-300 hover:text-white transition-colors flex items-center justify-center gap-1.5"
                >
                  <i className="fas fa-edit text-xs"></i>
                  <span>Rename</span>
                </button>
                <button
                  onClick={openDeleteModal}
                  className="flex-1 py-1.5 px-2 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-400/20 rounded-lg text-xs text-rose-300 hover:text-rose-200 transition-colors flex items-center justify-center gap-1.5"
                >
                  <i className="fas fa-trash-alt text-xs"></i>
                  <span>Delete</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Status Bar */}
      <div className="p-2 bg-white/5 border-t border-white/10 text-xs text-white/60 flex justify-between rounded-b-lg">
        <span>Pink Pixel Virtual File System</span>
        <span>{itemCount} item{itemCount === 1 ? '' : 's'} in {breadcrumbs[breadcrumbs.length - 1] || 'Root'}</span>
      </div>
    </div>
  )
}

export default ExplorerApp